import { unsafeCSS, html, nothing } from 'lit';
import { state, property, queryAll, query } from 'lit/decorators.js';

import { PopBaseComponent } from '../../../base/pop-base-component.js';
import { Constants as ProductCenterTableWrapperConstants } from '../../product-center-table-wrapper/product-center-table-wrapper.constants.js';
import { Constants } from './documents-table.constants.js';
import {
  CurrentSort,
  Disclaimer,
  DisclaimerPositioning,
  type IPCT,
  Product,
  SortDirection,
  TableRecordVariant,
} from '../../product-center-table-wrapper/product-center-table-wrapper.model.js';
import { getCurrencySymbol } from '../../../common/utils/currency.js';
import { buildShareClassDescriptor } from '../../../common/utils/buildShareClass.js';
import { zustandStateToComponent } from '../../store/product-center.store';
import { TableTypeEnum } from '../../store/product-center.store.model';
import {
  checkProductCallback,
  handleCommonSortByDropdownChange,
  handleSortProductDataCells,
  handleUpdate,
  renderProductCellData,
  renderProductHeader,
  renderReportButton,
  renderTableCells,
  renderTableHeader,
  setAnalyticsAttributesForBeaconIconButton,
  getReportButtonAriaLabel,
  getProductIdentifier,
  getProductIdentifierLabel,
} from '../../product-center-table-wrapper/product-center-table-wrapper.utils.js';
import Locale from '../../../common/utils/locale.js';

import style from '../common/css/table.scss?inline';
import {
  paginateTable,
  renderPagination,
  sortProductDataCells,
} from '../common/helper/table-helper.js';

/**
 * @tag pop-pc-documents-table-component
 * @property {ProductCenterExhibit} [componentObject] Documents Table content
 * @attribute {ProductCenterExhibit} [data-component-object] Documents Table content
 */
export class PopDocumentsTableComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  static readonly tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  @property()
  private readonly ipct: IPCT = {};

  /**
   * @internal
   */
  @property()
  private filteredProducts: Product[] = [];

  /**
   * @internal
   */
  @property()
  private sortByDropdownValue =
    ProductCenterTableWrapperConstants.DEFAULT_SORT_BY_DROP_DOWN_OPTION.value;

  /**
   * @internal
   */
  @property()
  selectedProducts: Product['productCode'][] = [];

  /**
   * @internal
   */
  private readonly tmdDisclaimers: Disclaimer[] = [];

  /**
   * @internal
   */
  private readonly productProfileDisclaimers: Disclaimer[] = [];

  @state()
  public sortDirectionState: SortDirection = SortDirection.ASCENDING;

  @property()
  tableHeaders: NodeListOf<Element> | undefined;

  @queryAll('beacon-icon-button.report-button')
  beaconIconButtons!: NodeListOf<HTMLElement>;

  @query('#documents-pagination-table')
  documentsPaginationTable!: HTMLElement;

  @query('#documents-pagination')
  documentsPagination!: HTMLElement;

  private readonly currentSort?: CurrentSort;

  constructor() {
    super();
    this.enableI18n = true;
  }

  /*
   * Component Methods
   */

  firstUpdated() {
    this.beaconIconButtons.forEach((button) => {
      setAnalyticsAttributesForBeaconIconButton(button);
    });
  }

  update(changedProperties: Map<string, unknown>) {
    handleUpdate(this, changedProperties);
    super.update(changedProperties);
  }

  private sortByDropdownChangeHandler(e: CustomEvent) {
    this.sortByDropdownValue = handleCommonSortByDropdownChange(
      e,
      ProductCenterTableWrapperConstants.DEFAULT_SORT_BY_DROP_DOWN_OPTION.value,
      this.tableHeaders,
      this.requestUpdate.bind(this),
    );
  }

  private sortProductDataCells(e: CustomEvent) {
    handleSortProductDataCells(this, e);
  }

  private renderTableHead() {
    return html`
      <tr>
        ${renderProductHeader(
          {
            rowspan: 1,
            sortByDropdownValue: this.sortByDropdownValue,
            sortByCallBack: this.sortByDropdownChangeHandler,
            onSort: this.sortProductDataCells,
            tableType: TableTypeEnum.DOCUMENTS,
          },
          new Locale(this.language, this.country),
        )}
        ${renderTableHeader({
          columnId: Constants.DOCUMENTS_COLUMN_IDS.PRODUCT_DISCLOSURE_STATEMENT,
          headerName: `${this.localize(Constants.TRANSLATION_KEYS.PRODUCT_DISCLOSURE_STATEMENT)}`,
          colspan: 1,
          rowspan: 2,
          parentHeader: null,
          sortable: false,
          customClass:
            ProductCenterTableWrapperConstants.CUSTOM_CLASSES.HEADER_ALIGNMENT,
        })}
        ${renderTableHeader({
          columnId: Constants.DOCUMENTS_COLUMN_IDS.TARGET_MARKET_DETERMINATION,
          headerName: `${this.localize(Constants.TRANSLATION_KEYS.TARGET_MARKET_DETERMINATION)}`,
          colspan: 1,
          rowspan: 2,
          parentHeader: null,
          sortable: false,
          customClass:
            ProductCenterTableWrapperConstants.CUSTOM_CLASSES.HEADER_ALIGNMENT,
          infoText: `${this.tmdDisclaimers.map((disclaimer) => disclaimer.disclaimerText.html).join('')}`,
        })}
        ${renderTableHeader({
          columnId: Constants.DOCUMENTS_COLUMN_IDS.PERFORMANCE_REPORT,
          headerName: `${this.localize(Constants.TRANSLATION_KEYS.PERFORMANCE_REPORT)}`,
          colspan: 1,
          rowspan: 2,
          parentHeader: null,
          sortable: false,
          customClass:
            ProductCenterTableWrapperConstants.CUSTOM_CLASSES.HEADER_ALIGNMENT,
        })}
        ${renderTableHeader({
          columnId: Constants.DOCUMENTS_COLUMN_IDS.PRODUCT_PROFILE,
          headerName: this.localize(Constants.TRANSLATION_KEYS.PRODUCT_PROFILE),
          colspan: 1,
          rowspan: 2,
          parentHeader: null,
          sortable: false,
          customClass:
            ProductCenterTableWrapperConstants.CUSTOM_CLASSES.HEADER_ALIGNMENT,
          infoText: `${this.productProfileDisclaimers.map((disclaimer) => disclaimer.disclaimerText.html).join('')}`,
        })}
        ${renderTableHeader({
          columnId: Constants.DOCUMENTS_COLUMN_IDS.QUARTERLY_REVIEW,
          headerName: this.localize(
            Constants.TRANSLATION_KEYS.QUARTERLY_REVIEW,
          ),
          colspan: 1,
          rowspan: 2,
          parentHeader: null,
          sortable: false,
          customClass:
            ProductCenterTableWrapperConstants.CUSTOM_CLASSES.HEADER_ALIGNMENT,
        })}
      </tr>
    `;
  }

  private renderTableBody() {
    if (!this.currentSort) {
      // Apply default sorting by shareClass when initializing
      this.filteredProducts = sortProductDataCells(
        undefined,
        this.filteredProducts,
        ProductCenterTableWrapperConstants.DEFAULT_SORT_BY_DROP_DOWN_OPTION
          .value,
      );
    }
    return html`
      ${this.filteredProducts.map((product: Product) => {
        return html`
          <tr>
            ${renderProductCellData({
              index: JSON.stringify(product.productName),
              sortValue: product.productName[this.sortByDropdownValue] ?? '',
              checkboxLabel: product.productName.vehicleTypeName,
              fundName: product.productName.fundName,
              shareClass: product.productName.shareClass
                ? buildShareClassDescriptor(product.productName.shareClass)
                : '',
              currencySymbol: getCurrencySymbol(
                product.productName.baseCurrencyName,
              ),
              currencyName: product.productName.baseCurrencyName,
              productIdentifier: getProductIdentifier(product, this.ipct),
              productCode: product.productCode,
              checkProductCallBack: (event: Event) =>
                checkProductCallback(event, TableTypeEnum.DOCUMENTS),
              isChecked: this.selectedProducts.includes(product.productCode),
              fundLink: product.productName.fundLink,
              showPopCheckbox: false,
              disclaimers: product.disclaimers ?? undefined,
              productIdentifierLabel: this.localize(
                getProductIdentifierLabel(product, this.ipct),
              ),
              noticeLabel: this.localize(
                ProductCenterTableWrapperConstants.TRANSLATION_KEYS.NOTICE,
              ),
            })}
            ${renderTableCells({
              sortValue: undefined,
              variant: TableRecordVariant.TEXT,
              cellClass: Constants.REPORT_ROW_CLASS,
              value: renderReportButton(
                product.documents?.productDisclosureStatement,
                getReportButtonAriaLabel(
                  product,
                  this.localize(
                    Constants.TRANSLATION_KEYS.PRODUCT_DISCLOSURE_STATEMENT,
                  ),
                ),
                this.localize(
                  Constants.TRANSLATION_KEYS.PRODUCT_DISCLOSURE_STATEMENT,
                ),
              ),
            })}
            ${renderTableCells({
              sortValue: undefined,
              variant: TableRecordVariant.TEXT,
              cellClass: Constants.REPORT_ROW_CLASS,
              value: renderReportButton(
                product.documents?.targetMarketDetermination,
                getReportButtonAriaLabel(
                  product,
                  this.localize(
                    Constants.TRANSLATION_KEYS.TARGET_MARKET_DETERMINATION,
                  ),
                ),
                this.localize(
                  Constants.TRANSLATION_KEYS.TARGET_MARKET_DETERMINATION,
                ),
              ),
            })}
            ${renderTableCells({
              sortValue: undefined,
              variant: TableRecordVariant.TEXT,
              cellClass: Constants.REPORT_ROW_CLASS,
              value: renderReportButton(
                product.pricing?.performanceReportURL,
                getReportButtonAriaLabel(
                  product,
                  this.localize(Constants.TRANSLATION_KEYS.PERFORMANCE_REPORT),
                ),
                this.localize(Constants.TRANSLATION_KEYS.PERFORMANCE_REPORT),
              ),
            })}
            ${renderTableCells({
              sortValue: undefined,
              variant: TableRecordVariant.TEXT,
              cellClass: Constants.REPORT_ROW_CLASS,
              value: renderReportButton(
                product.documents?.fundProfile,
                getReportButtonAriaLabel(
                  product,
                  this.localize(Constants.TRANSLATION_KEYS.PRODUCT_PROFILE),
                ),
                this.localize(Constants.TRANSLATION_KEYS.PRODUCT_PROFILE),
              ),
            })}
            ${renderTableCells({
              sortValue: undefined,
              variant: TableRecordVariant.TEXT,
              cellClass: Constants.REPORT_ROW_CLASS,
              value: renderReportButton(
                product.pricing?.quarterlyReportURL,
                getReportButtonAriaLabel(
                  product,
                  this.localize(Constants.TRANSLATION_KEYS.QUARTERLY_REVIEW),
                ),
                this.localize(Constants.TRANSLATION_KEYS.QUARTERLY_REVIEW),
              ),
            })}
          </tr>
        `;
      })}
    `;
  }

  private applyPagination() {
    paginateTable(this.documentsPaginationTable, this.documentsPagination);
  }

  renderComponent() {
    return html` <beacon-table
        defer-until="visible"
        variant="fixed"
        density="standard"
        id="${ProductCenterTableWrapperConstants.TABLE_FIELD
          .DOCUMENTS}-pagination-table"
        @beacon-event-element-ready="${this.applyPagination}"
      >
        <table>
          <thead>
            ${this.renderTableHead()}
          </thead>
          <tbody>
            ${this.renderTableBody()}
          </tbody>
        </table>
      </beacon-table>
      ${this.filteredProducts.length >
      ProductCenterTableWrapperConstants.PAGINATION_TABLE.DEFAULT_PAGE_SIZE
        ? renderPagination(
            this.filteredProducts,
            ProductCenterTableWrapperConstants.TABLE_FIELD.DOCUMENTS,
            this.applyPagination,
          )
        : nothing}`;
  }

  render() {
    if (!this.filteredProducts && !this.componentObject) {
      return html``;
    }
    return this.renderComponent();
  }
}

customElements.define(
  Constants.TAG_NAME,
  zustandStateToComponent(
    {
      mapState: (productCenterState) => {
        return {
          ipct: productCenterState.originalProductCenterObject.ipct,
          filteredProducts: productCenterState.getFilteredProducts(),
          selectedProducts:
            productCenterState.selectedProducts[TableTypeEnum.DOCUMENTS],
          tmdDisclaimers: productCenterState.getDisclaimersByPositioning(
            DisclaimerPositioning.TARGET_MARKET_DETERMINATION_TOOL_TIP,
          ),
          productProfileDisclaimers:
            productCenterState.getDisclaimersByPositioning(
              DisclaimerPositioning.PRODUCT_PROFILE_TOOL_TIP,
            ),
        };
      },
    },
    PopDocumentsTableComponent,
  ),
);
